import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
	Button,
	Divider,
	Flex,
	Form,
	Input,
	Modal,
	Table,
	Tabs,
	Typography,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';

import {
	useCreateCollectionAndAddVideoIntoIt,
	useGetVideosCollectionsWithIds,
} from '@/api/monitor_collections';
import { useDebounce } from '@/hooks';
import AddRemoveVideoCollectionsTable from '@/pages/app/dashboard/video-insights-co-pilot/collections/Videos/AddRemoveVideoCollectionsTable';
import { addRemoveCollectionsColumns } from '@/pages/app/dashboard/video-insights-co-pilot/collections/Videos/data';
import { CollectionContext } from '@/providers/CollectionsContextProvider';
import { IPagination } from '@/ts';

import InfoPopover from '../common/InfoPopover';

export default function AddVideoToCollectionModal() {
	const {
		isAddToCollectionModalOpen,
		setIsAddToCollectionModalOpen,
		modalData,
	} = useContext(CollectionContext);

	const [form] = useForm();

	const [tabsActiveKey, setTabsActiveKey] = useState('1');
	const [sort, setSort] = useState<'created_at' | 'status'>('created_at');
	const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
	const [search, setSearch] = useState('');

	const debouncedSearch = useDebounce(search);

	const [pagination, setPagination] = useState<IPagination>({
		page: 1,
		pageCount: 1,
		total: 0,
		limit: 10,
	});

	// console.log("modalData", modalData);

	const {
		data: collectionsData,
		isLoading,
		isFetching,
	} = useGetVideosCollectionsWithIds({
		pagination,
		sortBy: sort,
		sortOrder,
		url: modalData?.url,
		enabled: isAddToCollectionModalOpen && !!modalData?.url,
		search: debouncedSearch.length >= 3 ? debouncedSearch : '',
	});

	const { mutate: addCollectionWithVideo, isLoading: isAddCollectionLoading } =
		useCreateCollectionAndAddVideoIntoIt();

	const items = collectionsData?.data?.data?.items || [];
	const total = collectionsData?.data?.data?.pagination?.total || 0;

	const onTableChange = (__: any, _: any, sorter: any) => {
		setSort(sorter.field ?? 'created_at');
		setSortOrder(sorter.order === 'ascend' ? 'asc' : 'desc');
	};

	const handleOk = async () => {
		try {
			await form.validateFields();

			const { title, description } = form.getFieldsValue();

			addCollectionWithVideo(
				{
					title,
					description,
					post_id: modalData?.id,
				},
				{
					onSuccess: () => {
						message.success('Added');
						setIsAddToCollectionModalOpen(false);
						form.resetFields();
						setTabsActiveKey('1');
						setSearch('');
						setPagination((cur) => ({ ...cur, page: 1 }));
					},
					onError: (error) => {
						const err = error as any;
						message.error(
							`An error occured: ${
								err?.response?.data?.message || err?.message
							}.`,
						);
						console.error('error', error);
					},
				},
			);
		} catch (error) {
			console.error('Error adding collection:', error);
			message.error('Please fill all required fields.');
		}
	};

	const handleCancel = () => {
		setPagination((cur) => ({ ...cur, page: 1 }));
		form.resetFields();
		setIsAddToCollectionModalOpen(false);
		setTabsActiveKey('1');
		setSearch('');
		setPagination((cur) => ({ ...cur, page: 1 }));
	};

	const columns = useMemo(() => addRemoveCollectionsColumns('videos'), []);

	return (
		<Modal
			title={'Add video to collection'}
			// centered
			width={800}
			open={isAddToCollectionModalOpen}
			onOk={handleOk}
			onCancel={handleCancel}
			closable
			// loading={isFetching || isLoading}
			maskClosable={false}
			footer={[
				<Button
					key="back"
					size="small"
					onClick={handleCancel}
					disabled={isAddCollectionLoading}
				>
					Close
				</Button>,
				tabsActiveKey === '2' && (
					<Button
						key="submit"
						size="small"
						type="primary"
						loading={isAddCollectionLoading}
						onClick={handleOk}
						disabled={isAddCollectionLoading}
					>
						Add
					</Button>
				),
			]}
		>
			<Flex justify="center" align="center" style={{ marginBottom: '1rem' }}>
				<InfoPopover record={modalData} isForModal />
			</Flex>

			<Tabs
				defaultActiveKey="1"
				size="small"
				style={{ width: '100%' }}
				activeKey={tabsActiveKey}
				onChange={setTabsActiveKey}
				items={[
					{
						key: '1',
						label: 'Existing Collections',
						children: (
							<Flex vertical gap={12}>
								<Typography.Title level={5} style={{ margin: 0 }}>
									Add/remove to/from existing collections
								</Typography.Title>
								<Input
									type="text"
									placeholder="Search..."
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									size="small"
								/>
								<AddRemoveVideoCollectionsTable
									key={modalData?.id}
									data={items}
									columns={columns}
									onTableChange={onTableChange}
									isLoading={isLoading}
									isFetching={isFetching}
									pagination={{ ...pagination, total }}
									setPagination={setPagination}
									videoId={modalData?.id}
								/>
							</Flex>
						),
					},
					{
						key: '2',
						label: 'New Collection',
						children: (
							<>
								<Typography.Title level={5} style={{ margin: '0 0 0.5rem' }}>
									Create a new collection with this video
								</Typography.Title>
								<Form
									form={form}
									layout="vertical"
									initialValues={{
										title: '',
										description: '',
									}}
									style={{ marginBottom: '1rem' }}
								>
									<Flex vertical gap={16}>
										<Form.Item
											name="title"
											label="Title"
											// layout="vertical"
											rules={[
												{
													required: true,
													message: 'Please provide collection name',
												},
												{
													whitespace: true,
													message: 'Collection title cannot be empty.',
												},
											]}
											style={{ margin: 0 }}
										>
											<Input
												placeholder="Collection name"
												style={{ resize: 'none' }}
											/>
										</Form.Item>

										<Form.Item
											label="Description"
											name="description"
											style={{ marginBottom: '1rem' }}
										>
											<TextArea
												placeholder="Collection description"
												style={{ resize: 'none' }}
												rows={2}
												maxLength={255}
												count={{
													show: true,
													max: 255,
												}}
											/>
										</Form.Item>
									</Flex>
								</Form>
							</>
						),
					},
				]}
			></Tabs>
		</Modal>
	);
}
